<template>
  <div
      id="app"
      class="h-100"
      :class="[skinClasses]"
  >
    <div
        v-if="
          !$route.path.includes('/files/terms') &&
          !$route.path.includes('/files/risk')&&
          !$route.path.includes('/read-files')
      "
    >
      <component :is="layout">
        <router-view/>
      </component>
    </div>
    <div v-else>
      <router-view/>
    </div>

    <div v-if="showRequiredSigningOverlay" class="required-signing-overlay">
      <div class="required-signing-modal">
        <h4 class="mb-1">{{ $t('required_signing_title') }}</h4>
        <p class="mb-2">
          {{ $t('required_signing_subtitle') }}
        </p>

        <div
          v-for="doc in requiredSigningPopup.documents"
          :key="doc.form_type + '-' + (doc.subject_id || 'user')"
          class="required-signing-row"
        >
          <div class="required-signing-content">
            <span>{{ getRequiredDocumentLabel(doc.form_type) }}</span>
            <small
              v-if="requiresAdminSigning(doc)"
              class="required-signing-admin-note"
            >
              {{ $t('required_signing_wait_admin') }}
            </small>
          </div>
          <span class="required-signing-status">{{ doc.status }}</span>
        </div>

        <div class="required-signing-buttons mt-2">
          <button
            class="btn btn-outline-secondary"
            @click="closeRequiredSigningPopup"
          >
            {{ $t('close') }}
          </button>
          <button
            class="btn btn-primary"
            :disabled="!hasActionableRequiredSigningDocument || hasUpdatedRequiredSigningStatus"
            @click="openSkribbleInstructions"
          >
            {{ $t('continue') }}
          </button>
        </div>
      </div>
    </div>

    <SkribblePopup
      :show="requiredSigningPopup.showSkribbleInstructions"
      @cancel="cancelSkribbleInstructions"
      @continue="continueToSigning"
    />
  </div>
</template>

<script>
import {$themeColors, $themeBreakpoints, $themeConfig} from '@themeConfig'
import {provideToast} from 'vue-toastification/composition'
import {watch} from '@vue/composition-api'
import useAppConfig from '@core/app-config/useAppConfig'
import {useWindowSize, useCssVar} from '@vueuse/core'
import store from '@/store'
import SkribblePopup from '@/components/skribble/SkribblePopup.vue'

const LayoutVertical = () => import('@/layouts/vertical/LayoutVertical.vue')
const LayoutHorizontal = () => import('@/layouts/horizontal/LayoutHorizontal.vue')
const LayoutFull = () => import('@/layouts/full/LayoutFull.vue')

export default {
  components: {
    LayoutHorizontal,
    LayoutVertical,
    LayoutFull,
    SkribblePopup,
  },
  data() {
    return {
      password: this.$store.state.app.password,
      enterPassword: null,
      countries_code: [],
      countries: [],
      showApp: false,
      requiredSigningPopup: {
        show: false,
        documents: [],
        nextSigningUrl: null,
        showSkribbleInstructions: false,
        dismissed: true,
      },
      requiredSigningPollInterval: null,
      loginTriggerTimeout: null,
    }
  },
  computed: {
    layout() {
      if (this.$route.meta.layout === 'full') return 'layout-full'
      return `layout-${this.contentLayoutType}`
    },
    contentLayoutType() {
      return this.$store.state.appConfig.layout.type
    },
    showRequiredSigningOverlay() {
      return this.requiredSigningPopup.show
        && !this.requiredSigningPopup.dismissed
        && !this.requiredSigningPopup.showSkribbleInstructions
    },
    hasUpdatedRequiredSigningStatus() {
      return (this.requiredSigningPopup.documents || []).some(this.requiresAdminSigning)
    },
    hasActionableRequiredSigningDocument() {
      return (this.requiredSigningPopup.documents || []).some(doc => !this.requiresAdminSigning(doc))
    },
  },
  beforeCreate() {
    const colors = [
      'primary',
      'secondary',
      'success',
      'info',
      'warning',
      'danger',
      'light',
      'dark',
    ]

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = colors.length; i < len; i++) {
      $themeColors[colors[i]] = useCssVar(
          `--${colors[i]}`,
          document.documentElement,
      ).value.trim()
    }

    // Set Theme Breakpoints
    const breakpoints = ['xs', 'sm', 'md', 'lg', 'xl']

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = breakpoints.length; i < len; i++) {
      $themeBreakpoints[breakpoints[i]] = Number(
          useCssVar(
              `--breakpoint-${breakpoints[i]}`,
              document.documentElement,
          ).value.slice(0, -2),
      )
    }

    // Set RTL
    const {isRTL} = $themeConfig.layout
    document.documentElement.setAttribute('dir', isRTL ? 'rtl' : 'ltr')
  },
  watch: {
    '$route'() {
      this.syncAuthSessionSideEffects()
    },
  },
  created() {
    localStorage.removeItem('hideSkribblePopup')
    this.$http.post('/countries').then(res => {
      res.data.value.map(item => {
        this.countries_code.push(item)
        this.countries.push({
          value: item.iso_code,
          text: `${item.iso_code} (${item.phone_code})`,
        })
      })
      setTimeout(() => {
        this.$store.commit('app/SET_COUNTRIES_LIST', this.countries)
        this.$store.commit('app/SET_COUNTRIES_CODE_LIST', this.countries_code)
      }, 3000)
    })
    this.syncAuthSessionSideEffects()
    if (typeof window !== 'undefined') {
      window.__aeqRequireSigningGate = () => this.blockActionForRequiredSigning()
    }
  },
  beforeDestroy() {
    this.stopRequiredSigningPolling()
    if (this.loginTriggerTimeout) {
      clearTimeout(this.loginTriggerTimeout)
      this.loginTriggerTimeout = null
    }
    if (typeof window !== 'undefined' && window.__aeqRequireSigningGate) {
      delete window.__aeqRequireSigningGate
    }
  },
  methods: {
    isGuestRoute() {
      return this.$route?.meta?.middleware === 'guest'
    },
    hasActiveAuthSession() {
      const { authenticated, token, user } = this.$store.state.auth
      return Boolean(
        authenticated
        && token
        && user
        && user.id,
      )
    },
    shouldRunAuthSessionChecks() {
      return this.hasActiveAuthSession() && !this.isGuestRoute()
    },
    syncAuthSessionSideEffects() {
      if (!this.shouldRunAuthSessionChecks()) {
        this.stopRequiredSigningPolling()
        this.requiredSigningPopup.show = false
        this.requiredSigningPopup.showSkribbleInstructions = false
        return
      }

      this.scheduleLoginTriggerCheck()
      this.fetchRequiredSigningState()
      this.startRequiredSigningPolling()
    },
    scheduleLoginTriggerCheck() {
      if (this.loginTriggerTimeout) {
        clearTimeout(this.loginTriggerTimeout)
      }

      this.loginTriggerTimeout = setTimeout(() => {
        if (!this.shouldRunAuthSessionChecks()) return

        const id = this.$store.state.auth.user?.id
        const token = this.$store.state.auth.token

        if (!id || !token) return

        this.$http
          .post(`/check_login_trigger/${id}`, { token })
          .then(res => {
            if (res.data && !res.data.value) {
              this.$store.commit('auth/SET_USER', [])
              this.$store.commit('auth/SET_AUTHENTICATED', false)
              this.$store.commit('auth/UPDATE_TOKEN', null)
              window.location.href = '/login'
            }
          })
          .catch(() => {})
      }, 3000)
    },
    stopRequiredSigningPolling() {
      if (this.requiredSigningPollInterval) {
        clearInterval(this.requiredSigningPollInterval)
        this.requiredSigningPollInterval = null
      }
    },
    requiresAdminSigning(doc) {
      return String(doc?.status || '').toUpperCase() === 'UPDATED'
    },
    async fetchRequiredSigningState() {
      if (!this.shouldRunAuthSessionChecks()) {
        this.requiredSigningPopup.show = false
        this.requiredSigningPopup.showSkribbleInstructions = false
        return
      }
      try {
        const { data } = await this.$http.get('/skribble/required-documents')
        const documents = data?.value?.documents || []
        const pendingDocuments = documents.filter(doc => doc.required && doc.status !== 'SIGNED')
        const nextSigningUrl = pendingDocuments.find(doc => doc.signing_url)?.signing_url || null

        this.requiredSigningPopup.documents = pendingDocuments
        this.requiredSigningPopup.nextSigningUrl = nextSigningUrl
        this.requiredSigningPopup.show = pendingDocuments.length > 0
        if (!this.requiredSigningPopup.show) {
          this.requiredSigningPopup.showSkribbleInstructions = false
          if (!this.requiredSigningPopup.dismissed){
            window.location.reload()
          }
          this.requiredSigningPopup.dismissed = true
        }
      } catch (e) {
        this.requiredSigningPopup.show = false
        this.requiredSigningPopup.showSkribbleInstructions = false
        this.requiredSigningPopup.dismissed = true
      }
    },
    startRequiredSigningPolling() {
      if (!this.shouldRunAuthSessionChecks()) return
      if (this.requiredSigningPollInterval) return

      this.requiredSigningPollInterval = setInterval(() => {
        this.fetchRequiredSigningState()
      }, 10000)
    },
    openSkribbleInstructions() {
      const targetDocument = this.getNextRequiredSigningDocument()
      if (!targetDocument) return
      this.requiredSigningPopup.show = false

      if (localStorage.getItem('hideSkribblePopup') === 'true') {
        this.continueToSigning()
        return
      }

      this.requiredSigningPopup.showSkribbleInstructions = true
    },
    cancelSkribbleInstructions() {
      this.requiredSigningPopup.showSkribbleInstructions = false
      this.requiredSigningPopup.show = true
    },
    async continueToSigning() {
      const targetDocument = this.getNextRequiredSigningDocument()
      if (!targetDocument) return
      this.requiredSigningPopup.showSkribbleInstructions = false
      let signingUrl = targetDocument.signing_url || null

      if (!signingUrl) {
        try {
          const { data } = await this.$http.post('/skribble/required-documents/continue', {
            form_type: targetDocument.form_type,
            lang: this.$i18n?.locale || 'de',
          })
          signingUrl = data?.value?.signing_url || null
          await this.fetchRequiredSigningState()
        } catch (e) {
          return
        }
      }

      if (signingUrl) {
        window.open(signingUrl, '_blank')
      }
    },
    getNextRequiredSigningDocument() {
      return (this.requiredSigningPopup.documents || []).find(doc => !this.requiresAdminSigning(doc)) || null
    },
    closeRequiredSigningPopup() {
      this.requiredSigningPopup.dismissed = true
      this.requiredSigningPopup.showSkribbleInstructions = false
    },
    blockActionForRequiredSigning() {
      const hasPendingDocuments = (this.requiredSigningPopup.documents || []).length > 0
      if (!hasPendingDocuments) return false
      this.requiredSigningPopup.dismissed = false
      this.requiredSigningPopup.show = true
      this.requiredSigningPopup.showSkribbleInstructions = false
      return true
    },
    getRequiredDocumentLabel(formType) {
      const labels = {
        framework_agreement_seller: this.$t('doc_framework_agreement'),
        framework_agreement_investor: this.$t('doc_framework_agreement'),
        investor_profile_agreement: this.$t('doc_investor_profile_agreement'),
        formRisk: this.$t('doc_risk_acknowledgement'),
        formA: this.$t('skr_form_title'),
        formK: this.$t('skr_formk_title'),
      }
      return labels[formType] || formType
    },
  },
  setup() {
    const {skin, skinClasses} = useAppConfig()
    if (skin.value === 'dark') document.body.classList.add('dark-layout')

    provideToast({
      hideProgressBar: true,
      closeOnClick: false,
      closeButton: false,
      icon: false,
      timeout: 3000,
      transition: 'Vue-Toastification__fade',
    })

    // Set Window Width in store
    store.commit('app/UPDATE_WINDOW_WIDTH', window.innerWidth)
    const {width: windowWidth} = useWindowSize()
    watch(windowWidth, val => {
      store.commit('app/UPDATE_WINDOW_WIDTH', val)
    })

    return {
      skinClasses,
    }
  },
}
</script>

<style scoped>
.required-signing-overlay {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 10000;
  background: rgba(0, 0, 0, 0.65);
  display: flex;
  align-items: center;
  justify-content: center;
}

.required-signing-modal {
  width: 92%;
  max-width: 640px;
  background: #fff;
  border-radius: 10px;
  padding: 1.5rem;
}

.required-signing-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
}

.required-signing-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  border: 1px solid #e8ebef;
  border-radius: 8px;
  padding: 0.6rem 0.8rem;
  margin-bottom: 0.6rem;
}

.required-signing-content {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}

.required-signing-admin-note {
  color: #6c757d;
}

.required-signing-status {
  font-size: 0.85rem;
  color: #3f4a57;
}

</style>
